import { Briefcase, Check, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import {
  useDataTable,
  type DataTablePage,
  type DataTableQuery,
} from '../../../../components/data-table/useDataTable';
import { ErrorState } from '../../../../components/ErrorState';
import type { FolderTab } from '../../../../components/FolderTabs';
import type { MenuItem } from '../../../../components/Menu';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { ShortcutButton } from '../../../../components/ShortcutButton';
import { StackedCell } from '../../../../components/table/StackedCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateTime } from '../../../../lib/formatDate';
import { ApiError } from '../../../../lib/apiError';
import { personDisplayName, usePersonNames } from '../../personnel/usePersonNames';
import { AddSlotsModal } from '../components/AddSlotsModal';
import { PlantillaPositionModal } from '../components/PlantillaPositionModal';
import { plantillaApi } from '../plantillaApi';
import {
  salaryTypeLabel,
  type PlantillaItemListItem,
  type PlantillaPositionListItem,
} from '../plantillaTypes';

type TabKey = 'info' | 'slots';

/**
 * One authorized post, on the **header** profile layout.
 *
 * A position reads as one continuous thing — a title, a unit, a grade, and the slots under
 * it — so it takes the header shape rather than the sidebar one. The slots are the substance,
 * which is why the anchor column on the index opens this page rather than the edit form.
 */
export function PlantillaPositionProfilePage() {
  const { id = '' } = useParams<{ id: string }>();

  const [position, setPosition] = useState<PlantillaPositionListItem | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // LANDS on Slots (product call, 19 Oct 2026): a post is opened to read its items. Info stays
  // the first tab DECLARED — only where the page lands moves.
  const [tab, setTab] = useState<TabKey>('slots');
  const [editing, setEditing] = useState(false);
  const [addingSlots, setAddingSlots] = useState(false);
  const [showDeletedSlots, setShowDeletedSlots] = useState(false);
  const [slotAction, setSlotAction] = useState<{
    kind: 'remove' | 'restore';
    slot: PlantillaItemListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    plantillaApi
      .get(id, controller.signal)
      .then((loaded) => {
        setPosition(loaded);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoadError(caught instanceof ApiError ? caught : new ApiError('The position could not be loaded.', 0));
      });

    return () => controller.abort();
  }, [id, reloadKey]);

  /*
    The slots are a table of records inside a profile, so they go through <DataTablePanel> —
    search, sort, resize and paging — rather than a hand-rolled list. The endpoint returns
    every slot at once, so the paging and sorting happen here over the full set rather than
    over one page of it, which is the distinction the rule actually cares about.
  */
  const fetchSlots = useCallback(
    async (query: DataTableQuery, signal: AbortSignal): Promise<DataTablePage<PlantillaItemListItem>> => {
      const all = await plantillaApi.slots(id, showDeletedSlots, signal);

      const term = (query.search ?? '').trim().toLowerCase();
      const matched = term ? all.filter((slot) => slot.itemNumber.toLowerCase().includes(term)) : all;

      const [field, direction] = (query.sort ?? 'itemNumber').split(':');
      const descending = direction === 'desc';

      const sorted = [...matched].sort((a, b) => {
        const compared =
          field === 'status'
            ? Number(a.isActive) - Number(b.isActive)
            : a.sequenceNumber - b.sequenceNumber;
        return descending ? -compared : compared;
      });

      const start = (query.page - 1) * query.pageSize;
      return { items: sorted.slice(start, start + query.pageSize), total: sorted.length };
    },
    [id, showDeletedSlots],
  );

  const slotsTable = useDataTable<PlantillaItemListItem>({
    fetchPage: fetchSlots,
    defaultSort: 'itemNumber',
  });

  /*
    One batch resolve for the slots on screen — the composition rule. The rows carry the
    holder's personId and no name. Without personnel.read the server sends no holder at all,
    so there is nothing to resolve and the call is not made.
  */
  const canReadPeople = usePermission('personnel.read');
  const holderIds = useMemo(
    () =>
      canReadPeople
        ? slotsTable.rows.map((slot) => slot.personId).filter((personId): personId is string => personId != null)
        : [],
    [canReadPeople, slotsTable.rows],
  );
  const names = usePersonNames(holderIds);

  async function runSlotAction() {
    if (!slotAction) return;

    setRunning(true);
    setActionError(null);

    try {
      if (slotAction.kind === 'remove') await plantillaApi.removeSlot(slotAction.slot.id);
      else await plantillaApi.restoreSlot(slotAction.slot.id);

      setSlotAction(null);
      slotsTable.reload();
      reload();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const slotColumns: DataColumn<PlantillaItemListItem>[] = [
    {
      key: 'itemNumber',
      header: 'Item No',
      width: 190,
      sortable: true,
      render: (slot) => <TextCell value={slot.itemNumber} mono />,
    },
    {
      key: 'employee',
      header: 'Employee',
      width: 240,
      /*
        The holder's name, linked to their 201 file, with the employee number underneath.
        `None` for a vacant slot. `Hidden` when the slot is filled but the server sent no
        holder — this reader lacks personnel.read. `Restricted` (via personDisplayName, which
        tests the missing personId first) when the holder is above their access level; that
        one is not linked, because the record would refuse them.
      */
      render: (slot) => {
        if (!slot.isFilled) return <TextCell value="None" />;
        if (slot.personnelId == null) return <TextCell value="Hidden" />;

        const name = personDisplayName({ personId: slot.personId, employeeNumber: slot.employeeNumber ?? '' }, names);

        return (
          <StackedCell
            primary={
              slot.personId == null ? (
                name
              ) : (
                <Link to={`/personnel/${slot.personnelId}`} className="font-medium text-slate-800 hover:underline">
                  {name}
                </Link>
              )
            }
            secondary={slot.employeeNumber ?? undefined}
            mono
          />
        );
      },
    },
    {
      // Two columns, not one cell holding both: a cell renders exactly one value, and each of
      // these is sortable and filterable on its own.
      key: 'createdBy',
      header: 'Created by',
      width: 180,
      render: (slot) => <TextCell value={slot.createdByName} size="xs" />,
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: 190,
      render: (slot) => <TextCell value={formatDateTime(slot.createdAt)} mono size="xs" />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      sortable: true,
      status: true,
      render: (slot) =>
        slot.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Removed</StatusText>
        ),
    },
  ];

  function slotMenuItems(slot: PlantillaItemListItem): MenuItem[] {
    return [
      {
        key: `remove-${slot.id}`,
        label: 'Remove',
        icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'plantilla.manage-slots',
        destructive: true,
        disabledReason: !slot.isActive
          ? 'This slot is already removed.'
          : slot.isFilled
            ? 'This slot is filled. Vacate it before removing it.'
            : null,
        onClick: () => setSlotAction({ kind: 'remove', slot }),
      },
      {
        key: `restore-${slot.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'plantilla.manage-slots',
        disabledReason: slot.isActive ? 'This slot is already active.' : null,
        onClick: () => setSlotAction({ kind: 'restore', slot }),
      },
    ];
  }

  const tabs: FolderTab<TabKey>[] = [
    { key: 'info', label: 'Info' },
    {
      key: 'slots',
      label: (
        <>
          Slots
          <CountBadge count={position?.authorizedSlots ?? 0} tone="slate" />
        </>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <Breadcrumb
        segments={[
          'Human Resources',
          { label: 'Plantilla', to: '/plantilla' },
          position ? position.title : '',
        ]}
      />

      {loadError ? (
        <ErrorState
          message={loadError.message}
          correlationId={loadError.correlationId}
          onRetry={loadError.isNotFound ? undefined : reload}
        />
      ) : !position ? (
        <ProfileHeaderSkeleton tabs={tabs.length} />
      ) : (
        <ProfileHeaderShell<TabKey>
          id="plantilla-position-profile"
          tabs={tabs}
          activeTab={tab}
          onTabChange={setTab}
          panel={tab === 'slots' ? 'bleed' : 'cards'}
          header={
            <ProfileHeader
              id="plantilla-position-profile-header"
              icon={<Briefcase className="h-5 w-5" />}
              title={position.title}
              // The grade is the Info card's — the card can name the salary type outright.
              meta={[position.code, position.orgUnitName]}
              status={
                position.isActive ? (
                  <StatusText tone="active">Active</StatusText>
                ) : (
                  <StatusText tone="inactive">Deleted</StatusText>
                )
              }
              createdBy={position.createdByName}
              createdAt={position.createdAt}
            />
          }
          toolbar={
            <PermissionGate permission="plantilla.manage-slots">
              <ShortcutButton
                id="plantilla-position-add-slots"
                size="xs"
                icon={<Plus className="h-3.5 w-3.5" />}
                abbr="SLT"
                label="Add slots"
                tooltip="Authorize more slots under this position"
                disabledReason={
                  position.isActive ? null : 'This position is deleted. Restore it before authorizing more slots.'
                }
                onClick={() => setAddingSlots(true)}
              />
            </PermissionGate>
          }
          banner={
            <ProfileBanner
              id="plantilla-position-profile-banner"
              message={
                position.isActive
                  ? `${position.authorizedSlots} slot${position.authorizedSlots === 1 ? '' : 's'} authorized, ${position.vacantSlots} vacant.`
                  : 'This position is deleted. Its slots went with it, and its code stays reserved.'
              }
            />
          }
        >
          {tab === 'info' ? (
            <>
              <ProfileCard
                id="plantilla-position-profile-basic"
                title="Basic Info"
                onEdit={() => setEditing(true)}
                editPermission="plantilla.update"
              >
                <PreviewTable variant="table-display" rows={basicRows(position)} />
              </ProfileCard>

              {/*
                Headcount is moved by adding and removing slots, never by typing a number into
                a field — so this card is read-only and the action lives in the toolbar.
              */}
              <ProfileCard id="plantilla-position-profile-slots-summary" title="Headcount">
                <PreviewTable variant="table-display" rows={headcountRows(position)} />
              </ProfileCard>

              {/* Never editable: a record of what happened. */}
              <ProfileCard id="plantilla-position-profile-audit" title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(position)} />
              </ProfileCard>
            </>
          ) : (
            /*
              Full height, so the pager sits on the bottom edge of the tab rather than
              floating up under a one-row table.
            */
            <div className="flex h-full min-w-0 flex-col p-4">
              <DataTablePanel
                id="plantilla-slots"
                state={slotsTable}
                columns={slotColumns}
                rowKey={(slot) => slot.id}
                searchPlaceholder="Search item numbers"
                emptyMessage="No slots authorized yet. Add slots to say how many people this post may hold."
                emptySearchMessage="No slot matches that item number."
                /* The provenance the list view leads with, and what offers the Table/List switch. */
                otherInfo={(slot) => slot}
                /*
                  The kebab's entries. The shell draws the gutter and `<RowActionMenu>` owns
                  the menu, so the right-click shortcut, the permission filtering and the
                  Other Info entry all come with it — on the table AND the list view, which a
                  column could only ever reach one of.
                */
                rowActions={slotMenuItems}
                fill
                /*
                  Show removed lives in the cog, not as a loose checkbox over the table — the
                  same place the index page keeps it, and the place a reader already looks for
                  what is narrowing a list.
                */
                settingsItems={[
                  {
                    key: 'show-removed',
                    label: 'Show removed',
                    icon: showDeletedSlots ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
                    onClick: () => setShowDeletedSlots((current) => !current),
                  },
                ]}
              />
            </div>
          )}
        </ProfileHeaderShell>
      )}

      {editing && position ? (
        <PlantillaPositionModal
          open
          editing={position}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : null}

      {addingSlots && position ? (
        <AddSlotsModal
          open
          positionId={position.id}
          onClose={() => setAddingSlots(false)}
          onSaved={() => {
            setAddingSlots(false);
            slotsTable.reload();
            reload();
          }}
        />
      ) : null}

      {slotAction ? (
        <ConfirmDialog
          open
          onClose={() => {
            setSlotAction(null);
            setActionError(null);
          }}
          onConfirm={() => void runSlotAction()}
          title={slotAction.kind === 'remove' ? 'Remove slot' : 'Restore slot'}
          danger={slotAction.kind === 'remove'}
          loading={running}
          id={`plantilla-slot-${slotAction.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {slotAction.kind === 'remove'
                  ? `${slotAction.slot.itemNumber} stops being an authorized seat. Its number is not freed — a later slot takes the next number up, never this one.`
                  : `${slotAction.slot.itemNumber} becomes an authorized seat again, under its original number.`}
              </p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

/**
 * What the header does not already say.
 *
 * The title, the code and the unit are the header's — repeating them here would be the same
 * value in two slots. The grade lives here rather than in the header line because the card
 * can state the salary type outright, where the compact label leaves General implicit.
 */
function basicRows(position: PlantillaPositionListItem): PreviewRow[] {
  return [
    { label: 'Salary grade', value: position.salaryGradeLabel },
    {
      label: 'Type',
      value: position.salaryGrade == null ? null : salaryTypeLabel(position.salaryGradeType),
    },
    { label: 'Order', value: <span className="font-mono">{position.sortOrder}</span> },
    { label: 'Description', value: position.description },
  ];
}

function headcountRows(position: PlantillaPositionListItem): PreviewRow[] {
  return [
    { label: 'Authorized', value: <span className="font-mono">{position.authorizedSlots}</span> },
    { label: 'Filled', value: <span className="font-mono">{position.filledSlots}</span> },
    { label: 'Vacant', value: <span className="font-mono">{position.vacantSlots}</span> },
  ];
}

/**
 * The rest of the trail.
 *
 * Created-by and created-at are the header's on this layout, on screen for every tab — so
 * this card carries only what the header does not, which is the update half.
 */
function auditRows(position: PlantillaPositionListItem): PreviewRow[] {
  return [
    { label: 'Updated by', value: position.updatedByName },
    {
      label: 'Updated',
      value: position.updatedAt ? (
        <span className="font-mono">{formatDateTime(position.updatedAt)}</span>
      ) : null,
    },
  ];
}
